/**
 * Language detection for BeraStays (web, PWA, Android, iOS).
 *
 * Rules:
 *  1. Manual choice saved by the user always wins (kept across sessions).
 *  2. Otherwise, country detected by IP decides: Portuguese-speaking country -> pt, any other -> en.
 *  3. French/Chinese only become the default when the device language is fr/zh.
 *  4. Fallback: device language (when supported), else en.
 */

export const SUPPORTED_LANGUAGES = ['pt', 'en', 'fr', 'zh'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_STORAGE_KEY = 'berastays-language';
const MANUAL_FLAG_KEY = 'berastays-language-manual';
const GEO_DONE_KEY = 'berastays-language-geo-done';
const GEO_COUNTRY_KEY = 'berastays-language-country';

/** Portuguese-speaking countries/territories (ISO 3166-1 alpha-2). */
export const LUSOPHONE_COUNTRIES = new Set([
  'AO', // Angola
  'PT', // Portugal
  'BR', // Brasil
  'MZ', // Moçambique
  'CV', // Cabo Verde
  'GW', // Guiné-Bissau
  'ST', // São Tomé e Príncipe
  'TL', // Timor-Leste
  'GQ', // Guiné Equatorial (português é língua oficial)
  'MO', // Macau
]);

export const normalizeLanguage = (lng?: string | null): SupportedLanguage | undefined => {
  const base = (lng || '').split('-')[0].toLowerCase();
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(base)
    ? (base as SupportedLanguage)
    : undefined;
};

const safeGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore private-mode failures */
  }
};

/** The user explicitly picked a language in the selector at some point. */
export const hasManualLanguageChoice = (): boolean => {
  if (safeGet(MANUAL_FLAG_KEY) === '1') return true;

  // Backward compatibility: users who selected a language before the manual
  // flag existed only have the legacy stored language keys. Treat those as a
  // manual choice unless they were created by the new geo-detection flow.
  const legacyStoredLanguage = normalizeLanguage(safeGet(LANGUAGE_STORAGE_KEY)) || normalizeLanguage(safeGet('i18nextLng'));
  if (legacyStoredLanguage && safeGet(GEO_DONE_KEY) !== '1') {
    safeSet(MANUAL_FLAG_KEY, '1');
    return true;
  }

  return false;
};

export const getStoredLanguage = (): SupportedLanguage | undefined =>
  normalizeLanguage(safeGet(LANGUAGE_STORAGE_KEY)) || normalizeLanguage(safeGet('i18nextLng'));

/** Persist a manual selection so geolocation never overrides it again. */
export const markManualLanguage = (lng: string) => {
  const normalized = normalizeLanguage(lng) || 'pt';
  safeSet(MANUAL_FLAG_KEY, '1');
  safeSet(LANGUAGE_STORAGE_KEY, normalized);
  safeSet('i18nextLng', normalized);
};

export const getDeviceLanguage = (): SupportedLanguage | undefined => {
  if (typeof navigator === 'undefined') return undefined;
  const candidates = [
    ...(navigator.languages || []),
    navigator.language,
    (navigator as unknown as { userLanguage?: string }).userLanguage,
  ].filter(Boolean) as string[];
  for (const candidate of candidates) {
    const normalized = normalizeLanguage(candidate);
    if (normalized) return normalized;
  }
  return undefined;
};

export const languageForCountry = (
  countryCode?: string | null,
  deviceLanguage?: SupportedLanguage,
): SupportedLanguage => {
  const code = (countryCode || '').toUpperCase();
  if (code && LUSOPHONE_COUNTRIES.has(code)) return 'pt';
  // Outside the Portuguese-speaking world: keep fr/zh when that is the device language, else English.
  if (deviceLanguage === 'fr' || deviceLanguage === 'zh') return deviceLanguage;
  return 'en';
};

/** Country code cached from a previous IP lookup, when available. */
export const getCachedCountry = (): string | null => safeGet(GEO_COUNTRY_KEY);

/**
 * Synchronous best guess used for the very first render (no network).
 * Manual choice > cached country > device language > en.
 */
export const resolveInitialLanguage = (): SupportedLanguage => {
  if (hasManualLanguageChoice()) {
    return getStoredLanguage() || 'pt';
  }
  const device = getDeviceLanguage();
  const cachedCountry = getCachedCountry();
  if (cachedCountry) return languageForCountry(cachedCountry, device);
  const stored = getStoredLanguage();
  if (stored) return stored;
  return device || 'en';
};

/**
 * One-time IP country lookup. Resolves the language that matches the user's
 * country, or undefined when nothing should change.
 */
export const detectLanguageByLocation = async (): Promise<SupportedLanguage | undefined> => {
  if (hasManualLanguageChoice()) return undefined;
  if (safeGet(GEO_DONE_KEY) === '1') {
    const cached = getCachedCountry();
    return cached ? languageForCountry(cached, getDeviceLanguage()) : undefined;
  }

  try {
    const { getIpGeolocation } = await import('@/lib/ipGeolocation');
    const geo = await getIpGeolocation();
    safeSet(GEO_DONE_KEY, '1');
    if (!geo?.countryCode) return undefined;
    safeSet(GEO_COUNTRY_KEY, geo.countryCode.toUpperCase());
    return languageForCountry(geo.countryCode, getDeviceLanguage());
  } catch {
    return undefined;
  }
};

/** Maps the app language to a full locale tag for SEO/OG and formatting. */
export const localeTagFor = (lng?: string): string => {
  switch (normalizeLanguage(lng)) {
    case 'en':
      return 'en_US';
    case 'fr':
      return 'fr_FR';
    case 'zh':
      return 'zh_CN';
    default:
      return 'pt_AO';
  }
};

export const intlLocaleFor = (lng?: string): string => localeTagFor(lng).replace('_', '-');
